import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Trash2, Pause, Play, ExternalLink, AlertTriangle, Ban, ShieldAlert, Star, HelpCircle } from "lucide-react";

interface Props { sessionToken: string; actorName?: string }

const call = async (sessionToken: string, action: string, extra: Record<string, any> = {}, actorName?: string) => {
  const { data, error } = await supabase.functions.invoke("admin-p2p", {
    body: { action, sessionToken, actorName, ...extra },
  });
  if (error) throw new Error(error.message);
  if ((data as any)?.error) throw new Error((data as any).error);
  return data;
};

const AdminP2PTab = ({ sessionToken, actorName }: Props) => {
  const [verifs, setVerifs] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [trades, setTrades] = useState<any[]>([]);
  const [commissions, setCommissions] = useState<any[]>([]);
  const [fees, setFees] = useState<any[]>([]);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [balances, setBalances] = useState<any[]>([]);
  const [treasury, setTreasury] = useState<any>(null);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectAction, setRejectAction] = useState<"reject_verification" | "request_more_info" | "suspend_verification" | "ban_verification">("reject_verification");
  const [resolveTrade, setResolveTrade] = useState<any | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [auditFilter, setAuditFilter] = useState<string>("all");
  const [hotWalletStatus, setHotWalletStatus] = useState<any>(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [v, o, t, c, s, a, d, f, b, tr, w, hw] = await Promise.all([
        call(sessionToken, "list_verifications"),
        call(sessionToken, "list_offers"),
        call(sessionToken, "list_trades"),
        call(sessionToken, "list_commissions"),
        call(sessionToken, "get_settings"),
        call(sessionToken, "list_audit_log", { limit: 200 }),
        call(sessionToken, "list_deposits"),
        call(sessionToken, "list_fees"),
        call(sessionToken, "list_balances"),
        call(sessionToken, "get_treasury"),
        call(sessionToken, "list_withdrawals"),
        call(sessionToken, "get_hot_wallet_status"),
      ]);
      setVerifs((v as any).data ?? []);
      setOffers((o as any).data ?? []);
      setTrades((t as any).data ?? []);
      setCommissions((c as any).data ?? []);
      setSettings((s as any).data ?? { verification_fee_etb: 0, commission_pct: 0, fee_payment_instructions: "" });
      setAuditLog((a as any).data ?? []);
      setDeposits((d as any).data ?? []);
      setFees((f as any).data ?? []);
      setBalances((b as any).data ?? []);
      setTreasury((tr as any).data ?? null);
      setWithdrawals((w as any).data ?? []);
      setHotWalletStatus((hw as any).data ?? null);
    } catch (e: any) { toast.error(e.message); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);


  const approve = async (id: string, level: 2 | 3 = 2) => {
    try { await call(sessionToken, "approve_verification", { id, level }, actorName); toast.success(level === 3 ? "Approved at Level 3" : "Approved"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const promote = async (id: string) => {
    try { await call(sessionToken, "promote_verification", { id }, actorName); toast.success("Promoted to Level 3"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const openReject = (id: string, action: typeof rejectAction) => {
    setRejectId(id); setRejectAction(action); setRejectNote("");
  };
  const submitReject = async () => {
    if (!rejectId) return;
    try {
      await call(sessionToken, rejectAction, { id: rejectId, notes: rejectNote }, actorName);
      toast.success("Updated"); setRejectId(null); setRejectNote(""); loadAll();
    } catch (e: any) { toast.error(e.message); }
  };
  const setOfferStatus = async (id: string, status: string) => {
    try { await call(sessionToken, "set_offer_status", { id, status }, actorName); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const deleteOffer = async (id: string) => {
    if (!confirm("Delete this offer?")) return;
    try { await call(sessionToken, "delete_offer", { id }, actorName); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const saveSettings = async () => {
    try {
      await call(sessionToken, "update_settings", {
        verification_fee_etb: settings.verification_fee_etb,
        commission_pct: settings.commission_pct,
        fee_payment_instructions: settings.fee_payment_instructions,
        tron_admin_wallet: settings.tron_admin_wallet,
        flat_fee_usdt: settings.flat_fee_usdt,
        trade_expiry_minutes: settings.trade_expiry_minutes,
        hot_wallet_address: settings.hot_wallet_address,
        hot_wallet_per_tx_cap_usdt: settings.hot_wallet_per_tx_cap_usdt,
        hot_wallet_daily_cap_usdt: settings.hot_wallet_daily_cap_usdt,
      }, actorName);
      toast.success("Settings saved");
      loadAll();
    } catch (e: any) { toast.error(e.message); }
  };
  const depositAction = async (id: string, action: "verify_deposit" | "reject_deposit", notes?: string) => {
    try { await call(sessionToken, action, { id, notes }, actorName); toast.success("Updated"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const completeWithdrawal = async (id: string) => {
    const tx_hash = prompt("Tron tx hash for this payout (optional, but recommended)") ?? undefined;
    try { await call(sessionToken, "complete_withdrawal", { id, tx_hash }, actorName); toast.success("Marked completed"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const rejectWithdrawal = async (id: string) => {
    const notes = prompt("Reason for rejection (shown to user)") ?? undefined;
    try { await call(sessionToken, "reject_withdrawal", { id, notes }, actorName); toast.success("Rejected — balance refunded"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };
  const setFeeStatus = async (id: string, status: "collected" | "waived") => {
    try { await call(sessionToken, "set_fee_status", { id, status }, actorName); toast.success("Updated"); loadAll(); }
    catch (e: any) { toast.error(e.message); }
  };


  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="animate-spin w-5 h-5" /></div>;

  const pending = verifs.filter((v) => v.status === "pending" || (v.status === "approved" && v.admin_state));
  const reviewed = verifs.filter((v) => !(v.status === "pending" || (v.status === "approved" && v.admin_state)));

  const riskBadgeVariant = (level: string) =>
    level === "high" ? "destructive" : level === "medium" ? "secondary" : "outline";
  const pmLabel = (id: string) => ({ cbe: "CBE", cbe_birr: "CBE Birr", telebirr: "Telebirr", awash: "Awash", dashen: "Dashen", abyssinia: "Abyssinia" } as any)[id] ?? id;

  return (
    <div className="space-y-6 pt-4">
      <Tabs defaultValue="verifications">
        <TabsList>
          <TabsTrigger value="verifications">Verifications ({pending.length})</TabsTrigger>
          <TabsTrigger value="treasury">Treasury{treasury?.on_chain_error ? " ⚠" : ""}</TabsTrigger>
          <TabsTrigger value="balances">Balances ({balances.length})</TabsTrigger>
          <TabsTrigger value="deposits">Deposits ({deposits.filter((d:any)=>d.status==='pending').length})</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals ({withdrawals.filter((w:any)=>w.status==='pending').length})</TabsTrigger>
          <TabsTrigger value="offers">Offers ({offers.length})</TabsTrigger>
          <TabsTrigger value="trades">Trades ({trades.filter((t:any)=>t.status==='disputed').length ? `${trades.filter((t:any)=>t.status==='disputed').length}⚠` : trades.length})</TabsTrigger>
          <TabsTrigger value="commissions">Commissions ({commissions.filter((c:any)=>c.status==='owed').length})</TabsTrigger>
          <TabsTrigger value="fees">Fees ({fees.filter((f:any)=>f.status==='owed').length})</TabsTrigger>
          <TabsTrigger value="audit">Audit log</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>


        <TabsContent value="verifications" className="space-y-3">
          {pending.length === 0 && <p className="text-sm text-muted-foreground">No pending verifications.</p>}
          {pending.map((v) => (
            <Card key={v.id} className={v.risk_level === "high" ? "border-destructive/40" : ""}>
              <CardContent className="p-4 grid md:grid-cols-[1fr_auto] gap-4">
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {v.id_image_url && (
                      <a href={v.id_image_url} target="_blank" rel="noopener noreferrer">
                        <img src={v.id_image_url} alt="ID front" className="rounded border w-full object-cover max-h-32" />
                        <p className="text-[10px] text-center text-muted-foreground mt-0.5">Front</p>
                      </a>
                    )}
                    {v.id_image_back_url && (
                      <a href={v.id_image_back_url} target="_blank" rel="noopener noreferrer">
                        <img src={v.id_image_back_url} alt="ID back" className="rounded border w-full object-cover max-h-32" />
                        <p className="text-[10px] text-center text-muted-foreground mt-0.5">Back (QR)</p>
                      </a>
                    )}
                    {v.selfie_url && (
                      <a href={v.selfie_url} target="_blank" rel="noopener noreferrer">
                        <img src={v.selfie_url} alt="Selfie" className="rounded border w-full object-cover max-h-32" />
                        <p className="text-[10px] text-center text-muted-foreground mt-0.5">Selfie</p>
                      </a>
                    )}
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{v.full_name}</p>
                      <Badge variant="outline">Level {v.verification_level ?? 0}</Badge>
                      <Badge variant={riskBadgeVariant(v.risk_level)} className="capitalize">
                        Risk: {v.risk_level} ({v.risk_score})
                      </Badge>
                      {v.admin_state && (
                        <Badge variant="destructive" className="capitalize">{String(v.admin_state).replace("_", " ")}</Badge>
                      )}
                    </div>
                    <p>📞 {v.phone}{v.verified_phone && v.verified_phone === v.phone && <span className="text-emerald-600 ml-1">✓</span>}</p>
                    <p>✈️ @{v.telegram_username}</p>
                    {v.fayda_number && <p>🆔 Fayda: <b>{v.fayda_number}</b></p>}
                    {v.verified_payment_method && (
                      <p>💳 {pmLabel(v.verified_payment_method)} — <b>{v.verified_payment_name ?? "—"}</b>
                        {v.verified_payment_account_number && <> · <code>{v.verified_payment_account_number}</code></>}
                        {v.payment_verified && <span className="text-emerald-600 ml-1">✓</span>}
                      </p>
                    )}
                    {v.device_fingerprint && (
                      <p className="text-xs text-muted-foreground">Device: <code>{String(v.device_fingerprint).slice(0, 16)}…</code></p>
                    )}
                    <p className="text-xs text-muted-foreground break-all">QR (back): {v.qr_decoded_back || v.qr_decoded || "—"}</p>
                  </div>
                  {Array.isArray(v.duplicate_flags) && v.duplicate_flags.length > 0 && (
                    <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs space-y-1">
                      <p className="font-semibold text-destructive flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Duplicate-account flags
                      </p>
                      {v.duplicate_flags.map((f: any, i: number) => (
                        <p key={i} className="capitalize">• {String(f.type).replace("_", " ")}: <code>{f.value}</code></p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex md:flex-col gap-2 md:min-w-[180px]">
                  <Button size="sm" onClick={() => approve(v.id, 2)}><CheckCircle2 className="w-4 h-4 mr-1" />Approve (L2)</Button>
                  <Button size="sm" variant="secondary" onClick={() => approve(v.id, 3)}><Star className="w-4 h-4 mr-1" />Approve L3</Button>
                  <Button size="sm" variant="outline" onClick={() => openReject(v.id, "request_more_info")}><HelpCircle className="w-4 h-4 mr-1" />More info</Button>
                  <Button size="sm" variant="outline" onClick={() => openReject(v.id, "reject_verification")}><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => openReject(v.id, "suspend_verification")}><ShieldAlert className="w-4 h-4 mr-1" />Suspend</Button>
                  <Button size="sm" variant="destructive" onClick={() => openReject(v.id, "ban_verification")}><Ban className="w-4 h-4 mr-1" />Ban</Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {rejectId && (
            <Card className="border-destructive/30">
              <CardContent className="p-4 space-y-2">
                <Label>
                  {rejectAction === "request_more_info" ? "What additional info do you need?" :
                   rejectAction === "suspend_verification" ? "Suspension reason" :
                   rejectAction === "ban_verification" ? "Ban reason" : "Rejection reason"}
                </Label>
                <Textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Shown to the user as admin note…" />
                <div className="flex gap-2">
                  <Button variant="destructive" onClick={submitReject}>Confirm</Button>
                  <Button variant="ghost" onClick={() => setRejectId(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {reviewed.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-medium">Reviewed ({reviewed.length})</summary>
              <div className="mt-2 space-y-2">
                {reviewed.map((v) => (
                  <div key={v.id} className="flex items-center justify-between border rounded p-2 text-sm gap-2 flex-wrap">
                    <span>{v.full_name} · @{v.telegram_username}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">L{v.verification_level ?? 0}</Badge>
                      <Badge variant={riskBadgeVariant(v.risk_level)} className="capitalize">{v.risk_level}</Badge>
                      <Badge variant={v.status === "approved" ? "default" : "destructive"}>{v.status}</Badge>
                      {v.verification_level === 2 && !v.admin_state && (
                        <Button size="sm" variant="ghost" onClick={() => promote(v.id)}><Star className="w-3 h-3 mr-1" />→ L3</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </TabsContent>


        <TabsContent value="treasury" className="space-y-4">
          {!treasury && <p className="text-sm text-muted-foreground">Loading…</p>}
          {treasury && (
            <>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Platform Tron wallet</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground break-all">{treasury.wallet ?? "Not configured — set it in Settings"}</p>
                  {treasury.on_chain_error ? (
                    <p className="text-sm text-destructive flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {treasury.on_chain_error}</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">On-chain balance (live)</p>
                        <p className="text-2xl font-bold">{Number(treasury.on_chain_usdt).toFixed(2)} <span className="text-sm font-normal">USDT</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Owed to users</p>
                        <p className="text-2xl font-bold text-muted-foreground">{Number(treasury.owed_to_users).toFixed(2)} <span className="text-sm font-normal">USDT</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fees collected (lifetime)</p>
                        <p className="text-2xl font-bold">{Number(treasury.fees_collected).toFixed(2)} <span className="text-sm font-normal">USDT</span></p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Safely withdrawable</p>
                        <p className={`text-2xl font-bold ${Number(treasury.safely_withdrawable) < 0 ? "text-destructive" : ""}`}>
                          {Number(treasury.safely_withdrawable).toFixed(2)} <span className="text-sm font-normal">USDT</span>
                        </p>
                      </div>
                    </div>
                  )}
                  {treasury.fees_owed > 0 && (
                    <p className="text-xs text-muted-foreground">{Number(treasury.fees_owed).toFixed(2)} USDT in fees are recorded as "owed" (seller's wallet balance couldn't cover it at completion time) — see the Fees tab.</p>
                  )}
                  {treasury.on_chain_usdt !== null && Number(treasury.safely_withdrawable) < 0 && (
                    <p className="text-sm text-destructive">⚠ The on-chain balance is below what's owed to users — do not withdraw anything until this is reconciled.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    "Safely withdrawable" = live on-chain USDT balance − total owed to users (their available + locked balances). This wallet holds a pool of everyone's deposits plus your collected fees commingled — only withdraw up to this number, never the full on-chain balance.
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="balances" className="space-y-3">
          {balances.length === 0 && <p className="text-sm text-muted-foreground">No wallet balances yet.</p>}
          {balances.map((b) => (
            <Card key={b.user_id}>
              <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{b.user_name ?? b.user_id}</p>
                    <Badge variant="outline">L{b.verification_level ?? 0}</Badge>
                    {b.verification_status && (
                      <Badge variant={b.verification_status === "approved" ? "default" : "secondary"} className="capitalize">{b.verification_status}</Badge>
                    )}
                    {b.admin_state && <Badge variant="destructive" className="capitalize">{String(b.admin_state).replace("_", " ")}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">Updated {new Date(b.updated_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-4 text-right">
                  <div>
                    <p className="text-xs text-muted-foreground">Available</p>
                    <p className="font-bold">{Number(b.available_usdt).toFixed(2)} USDT</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Locked in trades</p>
                    <p className="font-bold text-muted-foreground">{Number(b.locked_usdt).toFixed(2)} USDT</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="deposits" className="space-y-3">
          {deposits.length === 0 && <p className="text-sm text-muted-foreground">No USDT deposits yet.</p>}
          {deposits.map((d) => (
            <Card key={d.id}>
              <CardContent className="p-4 grid md:grid-cols-[1fr_auto] gap-3 text-sm">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={d.status === "verified" ? "default" : d.status === "rejected" ? "destructive" : "secondary"} className="capitalize">{d.status}</Badge>
                    <span className="font-bold">{Number(d.amount_usdt).toFixed(2)} USDT</span>
                    <span className="text-muted-foreground">{d.user_name ?? d.user_id}</span>
                  </div>
                  <p className="text-xs break-all">
                    <a href={`https://tronscan.org/#/transaction/${d.tx_hash}`} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                      {d.tx_hash} <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                  {d.from_address && <p className="text-xs text-muted-foreground">From {d.from_address} → {d.to_address}</p>}
                  {d.admin_notes && <p className="text-xs text-destructive">{d.admin_notes}</p>}
                  <p className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()}</p>
                </div>
                {d.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => depositAction(d.id, "verify_deposit")}><CheckCircle2 className="w-4 h-4 mr-1" />Verify</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => {
                      const n = prompt("Reason for rejection (optional)"); depositAction(d.id, "reject_deposit", n ?? undefined);
                    }}><XCircle className="w-4 h-4" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="withdrawals" className="space-y-3">
          {hotWalletStatus && (
            <Card className={hotWalletStatus.per_tx_cap > 0 ? "border-emerald-500/30 bg-emerald-500/5" : "bg-muted/30"}>
              <CardContent className="p-3 text-xs">
                {hotWalletStatus.per_tx_cap > 0 && hotWalletStatus.daily_cap > 0 ? (
                  <p>
                    Hot-wallet auto-payout is <b>ON</b> — up to <b>{hotWalletStatus.per_tx_cap}</b> USDT/withdrawal, <b>{Number(hotWalletStatus.used_today).toFixed(2)} / {hotWalletStatus.daily_cap}</b> USDT used in the last 24h.
                    {hotWalletStatus.address && <> Wallet: <code>{hotWalletStatus.address}</code></>}
                  </p>
                ) : (
                  <p className="text-muted-foreground">Hot-wallet auto-payout is off — all withdrawals need manual approval below. Configure caps in Settings to enable it.</p>
                )}
              </CardContent>
            </Card>
          )}
          {withdrawals.length === 0 && <p className="text-sm text-muted-foreground">No withdrawal requests yet.</p>}
          {withdrawals.map((w) => (
            <Card key={w.id}>
              <CardContent className="p-4 grid md:grid-cols-[1fr_auto] gap-3 text-sm">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={w.status === "completed" ? "default" : w.status === "rejected" ? "destructive" : "secondary"} className="capitalize">{w.status}</Badge>
                    <span className="font-bold">{Number(w.amount_usdt).toFixed(2)} USDT</span>
                    <span className="text-muted-foreground">{w.user_name ?? w.user_id}</span>
                  </div>
                  <p className="text-xs break-all">To: <code>{w.to_address}</code></p>
                  {w.tx_hash && (
                    <p className="text-xs break-all">
                      <a href={`https://tronscan.org/#/transaction/${w.tx_hash}`} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                        {w.tx_hash} <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                  )}
                  {w.admin_notes && <p className="text-xs text-destructive">{w.admin_notes}</p>}
                  <p className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString()}</p>
                </div>
                {w.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => completeWithdrawal(w.id)}><CheckCircle2 className="w-4 h-4 mr-1" />Mark sent</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => rejectWithdrawal(w.id)}><XCircle className="w-4 h-4" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="offers" className="space-y-3">
          {offers.length === 0 && <p className="text-sm text-muted-foreground">No offers yet.</p>}

          {offers.map((o) => (
            <Card key={o.id}>
              <CardContent className="p-4 grid md:grid-cols-[1fr_auto] gap-3">
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={o.side === "sell" ? "default" : "secondary"}>{o.side}</Badge>
                    <span className="font-bold">{Number(o.price_etb_per_usdt).toLocaleString()} ETB/USDT</span>
                    <Badge variant="outline" className="capitalize">{o.status}</Badge>
                  </div>
                  <p className="text-muted-foreground">{o.owner ?? o.user_id} · Avail {o.available_usdt} · {o.min_amount_usdt}–{o.max_amount_usdt}</p>
                  <p className="text-xs">Methods: {(o.payment_methods ?? []).join(", ")} · TG: @{o.contact_telegram}</p>
                </div>
                <div className="flex gap-2">
                  {o.status === "active" ? (
                    <Button size="sm" variant="outline" onClick={() => setOfferStatus(o.id, "paused")}><Pause className="w-4 h-4" /></Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setOfferStatus(o.id, "active")}><Play className="w-4 h-4" /></Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => deleteOffer(o.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="trades" className="space-y-3">
          {trades.length === 0 && <p className="text-sm text-muted-foreground">No trades yet.</p>}
          {trades.map((t) => {
            const staleHours = t.status === "paid" && t.paid_at
              ? (Date.now() - new Date(t.paid_at).getTime()) / 36e5 : 0;
            const isStale = staleHours > 2;
            return (
            <Card key={t.id} className={t.status === "disputed" || isStale ? "border-destructive/50" : ""}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <Badge variant={t.status === "completed" ? "default" : t.status === "disputed" ? "destructive" : "secondary"} className="capitalize">{t.status.replace("_"," ")}</Badge>
                  {isStale && <Badge variant="destructive">⚠ Stale — seller hasn't released in {Math.floor(staleHours)}h</Badge>}
                  <span className="font-semibold">{Number(t.total_etb).toLocaleString()} ETB</span>
                  <span className="text-muted-foreground">· {t.amount_usdt} USDT @ {Number(t.price_etb_per_usdt).toLocaleString()}</span>
                  <Badge variant="outline">{t.payment_method}</Badge>
                  {Number(t.commission_etb) > 0 && <Badge variant="outline">Comm {Number(t.commission_etb).toLocaleString()} ETB</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  Buyer: {t.buyer_name ?? t.buyer_id} · Seller: {t.seller_name ?? t.seller_id} · {new Date(t.created_at).toLocaleString()}
                </p>
                {t.buyer_note && <p className="text-xs"><b>Buyer:</b> {t.buyer_note}</p>}
                {t.seller_note && <p className="text-xs"><b>Seller:</b> {t.seller_note}</p>}
                {t.dispute_reason && <p className="text-xs text-destructive"><b>Dispute:</b> {t.dispute_reason}</p>}
                {t.status === "disputed" && (
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" onClick={() => setResolveTrade({ ...t, _action: "release" })}>Release to seller</Button>
                    <Button size="sm" variant="outline" onClick={() => setResolveTrade({ ...t, _action: "refund" })}>Refund buyer</Button>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
          <Dialog open={!!resolveTrade} onOpenChange={(o) => !o && setResolveTrade(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>{resolveTrade?._action === "release" ? "Release USDT to seller" : "Refund buyer"}</DialogTitle></DialogHeader>
              <Textarea rows={3} value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Resolution notes (optional)" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setResolveTrade(null)}>Cancel</Button>
                <Button onClick={async () => {
                  try {
                    await call(sessionToken, "resolve_dispute", { id: resolveTrade.id, resolution: resolveTrade._action, notes: resolveNote }, actorName);
                    toast.success("Dispute resolved");
                    setResolveTrade(null); setResolveNote(""); loadAll();
                  } catch (e: any) { toast.error(e.message); }
                }}>Confirm</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="commissions" className="space-y-3">
          {commissions.length === 0 && <p className="text-sm text-muted-foreground">No commissions recorded yet.</p>}
          {commissions.map((c) => (
            <Card key={c.id}>
              <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div>
                  <p className="font-semibold">{Number(c.amount_etb).toLocaleString()} ETB <Badge variant={c.status === "collected" ? "default" : c.status === "waived" ? "outline" : "secondary"} className="ml-2 capitalize">{c.status}</Badge></p>
                  <p className="text-xs text-muted-foreground">Seller: {c.seller_name ?? c.seller_id} · Trade {c.trade_id.slice(0,8)} · {new Date(c.created_at).toLocaleString()}</p>
                  {c.notes && <p className="text-xs text-muted-foreground">{c.notes}</p>}
                </div>
                {c.status === "owed" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={async () => { try { await call(sessionToken, "set_commission_status", { id: c.id, status: "collected" }, actorName); toast.success("Marked collected"); loadAll(); } catch (e:any) { toast.error(e.message); } }}>Mark collected</Button>
                    <Button size="sm" variant="outline" onClick={async () => { try { await call(sessionToken, "set_commission_status", { id: c.id, status: "waived" }, actorName); toast.success("Waived"); loadAll(); } catch (e:any) { toast.error(e.message); } }}>Waive</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="fees" className="space-y-3">
          {fees.length === 0 && <p className="text-sm text-muted-foreground">No platform fees recorded yet.</p>}
          {fees.map((f) => (
            <Card key={f.id}>
              <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div>
                  <p className="font-semibold">{Number(f.amount_usdt).toFixed(2)} USDT <Badge variant={f.status === "collected" ? "default" : f.status === "waived" ? "outline" : "secondary"} className="ml-2 capitalize">{f.status}</Badge></p>
                  <p className="text-xs text-muted-foreground">Buyer: {f.payer_name ?? f.payer_id} · Trade {f.trade_id.slice(0,8)} · {new Date(f.created_at).toLocaleString()}</p>
                  {f.notes && <p className="text-xs text-muted-foreground">{f.notes}</p>}
                </div>
                {f.status === "owed" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setFeeStatus(f.id, "collected")}>Mark collected</Button>
                    <Button size="sm" variant="outline" onClick={() => setFeeStatus(f.id, "waived")}>Waive</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="audit" className="space-y-2">
          <Select value={auditFilter} onValueChange={setAuditFilter}>
            <SelectTrigger className="w-48 mb-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="verification">Verifications</SelectItem>
              <SelectItem value="offer">Offers</SelectItem>
              <SelectItem value="trade">Trades</SelectItem>
              <SelectItem value="deposit">Deposits</SelectItem>
              <SelectItem value="withdrawal">Withdrawals</SelectItem>
              <SelectItem value="settings">Settings</SelectItem>
              <SelectItem value="commission">Commissions</SelectItem>
              <SelectItem value="fee">Fees</SelectItem>
            </SelectContent>
          </Select>
          {auditLog.filter((r) => auditFilter === "all" || r.entity_type === auditFilter).length === 0 && (
            <p className="text-sm text-muted-foreground">No audit entries yet.</p>
          )}
          {auditLog.filter((r) => auditFilter === "all" || r.entity_type === auditFilter).map((r) => (
            <Card key={r.id}>
              <CardContent className="p-3 text-xs space-y-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{r.entity_type}</Badge>
                    <Badge variant={r.action.startsWith("status_change") ? "secondary" : "default"}>{r.action}</Badge>
                    <span className="text-muted-foreground">by <b>{r.actor_name}</b> ({r.actor_role})</span>
                  </div>
                  <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <p className="text-muted-foreground break-all">Entity: {r.entity_id}</p>
                {r.before && <p className="text-muted-foreground">Before: <code>{JSON.stringify(r.before)}</code></p>}
                {r.after && <p className="text-muted-foreground">After: <code>{JSON.stringify(r.after).slice(0, 200)}</code></p>}
                {r.notes && <p>📝 {r.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader><CardTitle>P2P monetization</CardTitle></CardHeader>
            <CardContent className="space-y-4 max-w-lg">
              <div>
                <Label>Verification fee (ETB)</Label>
                <Input type="number" step="0.01" value={settings?.verification_fee_etb ?? 0}
                  onChange={(e) => setSettings({ ...settings, verification_fee_etb: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">Charged once per user before they can post offers. Set 0 for free.</p>
              </div>
              <div>
                <Label>Trade commission (%)</Label>
                <Input type="number" step="0.01" value={settings?.commission_pct ?? 0}
                  onChange={(e) => setSettings({ ...settings, commission_pct: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">Shown to users on each offer. Collected manually for now via Telegram.</p>
              </div>
              <div>
                <Label>Fee payment instructions (shown to users)</Label>
                <Textarea rows={4} value={settings?.fee_payment_instructions ?? ""}
                  onChange={(e) => setSettings({ ...settings, fee_payment_instructions: e.target.value })}
                  placeholder="Send the fee to CBE 1000XXX, then send screenshot to @YourAdmin on Telegram." />
              </div>
              <div className="pt-4 border-t">
                <Label>Platform Tron (TRC20) wallet address</Label>
                <Input value={settings?.tron_admin_wallet ?? ""}
                  onChange={(e) => setSettings({ ...settings, tron_admin_wallet: e.target.value })}
                  placeholder="T..." />
                <p className="text-xs text-muted-foreground mt-1">Sellers deposit USDT here to fund their P2P sell offers.</p>
              </div>
              <div>
                <Label>Flat USDT fee per trade</Label>
                <Input type="number" step="0.01" value={settings?.flat_fee_usdt ?? 2}
                  onChange={(e) => setSettings({ ...settings, flat_fee_usdt: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">Charged to the buyer — credited to their platform balance net of this fee on each completed trade.</p>
              </div>
              <div>
                <Label>Trade payment window (minutes)</Label>
                <Input type="number" step="1" min="5" value={settings?.trade_expiry_minutes ?? 30}
                  onChange={(e) => setSettings({ ...settings, trade_expiry_minutes: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">If the buyer doesn't mark a trade paid within this window, it auto-cancels and the seller's USDT unlocks.</p>
              </div>
              <div className="pt-4 border-t">
                <Label>Hot wallet address (auto-payout)</Label>
                <Input value={settings?.hot_wallet_address ?? ""}
                  onChange={(e) => setSettings({ ...settings, hot_wallet_address: e.target.value })}
                  placeholder="T..." />
                <p className="text-xs text-muted-foreground mt-1">
                  Display only — fund this address with a small, capped USDT+TRX balance separate from your main wallet. Its private key is set as the <code>TRON_HOT_WALLET_PRIVATE_KEY</code> Supabase secret, never here.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Per-withdrawal auto-pay cap (USDT)</Label>
                  <Input type="number" step="1" min="0" value={settings?.hot_wallet_per_tx_cap_usdt ?? 0}
                    onChange={(e) => setSettings({ ...settings, hot_wallet_per_tx_cap_usdt: e.target.value })} />
                </div>
                <div>
                  <Label>Daily auto-pay cap (USDT)</Label>
                  <Input type="number" step="1" min="0" value={settings?.hot_wallet_daily_cap_usdt ?? 0}
                    onChange={(e) => setSettings({ ...settings, hot_wallet_daily_cap_usdt: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Both must be above 0 to enable auto-payout. Withdrawals over either cap fall back to manual approval in the Withdrawals tab.</p>
              <Button onClick={saveSettings}>Save</Button>

            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminP2PTab;
